import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services";
import { ApiError, type PlanTier } from "@/services/types";
import { isActiveTrial } from "@/features/billing/trialState";
import { beginGlobalActivity, waitForNextPaint } from "@/lib/networkActivity";
import { Button, Dialog, Input } from "@/ui/controls";
import { useBillingToasts } from "@/pages/dashboard/sections/useBillingToasts";
import { formatBillingPrice } from "@/features/billing/currencyFormat";
import { rememberCheckout } from "@/features/billing/CheckoutReturnContext";
import "@/pages/dashboard/sections/billing.css";

/** Professional plan selling points shown in the upgrade dialog. */
export const PRO_FEATURES = [
  "Group, collective & round-robin event types",
  "Unlimited connected calendars",
  "Team workspaces and participant scheduling",
  "Reusable booking forms",
  "Embeddable booking experiences",
  "Everything included in Free",
];

export function formatPrice(amountMinor: number, currency: string): string {
  return formatBillingPrice(amountMinor, currency);
}

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  planId?: string;
  /**
   * The tier the blocked feature needs. Picks which plan to offer when no explicit planId is
   * given, so a Team-only paywall cannot check the visitor out onto Professional.
   */
  requiredTier?: PlanTier;
  /** Optional context line (e.g. why the user hit the prompt) shown above the feature list. */
  headline?: string;
  /**
   * Price to show on the Upgrade button, in minor units, when it differs from the plan's own
   * amount. The white-label add-on is checked out as the pro_wl_* plan (a full $9 / $90 row) but
   * is sold as an *addition* to Professional, so the button should quote the difference — $3/month
   * or $30/year — not the whole plan. Omit for a plain plan upgrade; the plan's amount is used.
   */
  priceOverrideMinor?: number;
}

/**
 * The single, app-wide upgrade dialog. Self-contained: it fetches the billing overview itself
 * (shared ["billing-overview"] cache) and surfaces its own toasts, so it can be opened from
 * anywhere — the global UpgradePromptHost or the billing page — without prop wiring. Reuse this;
 * do not create additional upgrade dialogs.
 */
export function UpgradeModal({
  open,
  onClose,
  planId,
  requiredTier,
  headline,
  priceOverrideMinor,
}: UpgradeModalProps) {
  const toasts = useBillingToasts();
  const queryClient = useQueryClient();
  const [promoCode, setPromoCode] = useState("");
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const checkoutActivityEndRef = useRef<(() => void) | null>(null);

  // One full-screen loader that spans the whole commit — the change-plan call, the possible
  // hand-off to a fresh checkout when there's no in-place path (PLAN_CHANGE_REQUIRED), and the
  // redirect to the provider. Idempotent: the add-on path opens it on click and the checkout
  // step must not open a second one on top. Released only on a terminal state (redirect taken,
  // polling started, inline error).
  const beginCheckoutActivity = () => {
    if (checkoutActivityEndRef.current) return;
    // "immediate" so it paints on the same frame as the click, before request scheduling.
    checkoutActivityEndRef.current = beginGlobalActivity("immediate");
  };
  const endCheckoutActivity = () => {
    checkoutActivityEndRef.current?.();
    checkoutActivityEndRef.current = null;
  };

  useEffect(() => {
    // Browser Back can restore this document from the back/forward cache instead of remounting
    // React. In that case the checkout loader we deliberately kept alive for the outbound
    // navigation is restored too, covering the exact dashboard screen history brought back.
    // `pageshow` runs for both a normal load and a bfcache restore; ending an absent activity is
    // a no-op, while ending the retained one reveals the original route and component state.
    const releaseRestoredCheckoutActivity = () => endCheckoutActivity();
    window.addEventListener("pageshow", releaseRestoredCheckoutActivity);
    return () => window.removeEventListener("pageshow", releaseRestoredCheckoutActivity);
  }, []);

  // Only fetch the overview while the dialog is open (cheap, cached, shared with billing page).
  const overviewQuery = useQuery({
    queryKey: ["billing-overview"],
    queryFn: () => api.getBillingOverview(),
    enabled: open,
  });
  const plansQuery = useQuery({
    queryKey: ["billing-plans"],
    queryFn: () => api.listBillingPlans(),
    enabled: open,
    staleTime: 60_000,
  });
  // Without an explicit planId this fell through to the catalog default, which is a
  // Professional row — so a paywall raised by a Team-only feature offered the plan that does
  // not include it. requiredTier carries what the blocked feature actually needs.
  const selectedPlan = planId
    ? plansQuery.data?.find((candidate) => candidate.id === planId)
    : requiredTier
      ? plansQuery.data?.find((candidate) => candidate.tier === requiredTier)
      : plansQuery.data?.find((candidate) => candidate.defaultPlan) ?? plansQuery.data?.[0];
  const checkoutPlanId = selectedPlan?.id ?? planId;
  const plan = selectedPlan ?? overviewQuery.data?.plan;
  const upgradingFromTrial = isActiveTrial(overviewQuery.data?.state);

  // On a live paid plan, moving to a different paid plan is a change (in place, prorated), not a
  // new checkout. A trial or lapsed subscription still checks out. `changingPlan` also needs a
  // concrete target plan id to preview against.
  const subStatus = overviewQuery.data?.state?.status;
  const onLivePaidPlan =
    (subStatus === "ACTIVE" || subStatus === "PAST_DUE") &&
    (overviewQuery.data?.state?.entitled ?? false);
  const currentPlanCode = overviewQuery.data?.plan?.code;
  const targetPlanId = selectedPlan?.id;
  // A same-tier add-on ("remove BunnyCal branding" — checked out as pro_wl_* and always opened
  // with a price override for its own fixed price) keeps the tier and only turns a feature on.
  // It is a flat-price purchase, never an in-place blended/prorated change — the backend refuses
  // it from changePlan/previewPlanChange unconditionally now (PLAN_CHANGE_REQUIRED, every time)
  // and only accepts it through startCheckout, exempted there from the "switching plans" guard.
  // So it must NOT be caught by the cancel-then-switch block below, and must NOT be treated as
  // `changingPlan` — it goes through this modal's plain checkout branch like a brand-new
  // subscription would, just against a customer who already has one. The global paywall host
  // never routes here for it (it points at the Team plan), so a set price override uniquely
  // identifies the add-on.
  const isSameTierAddOn = priceOverrideMinor != null;
  // In-place plan change is gated off for this environment. An active paid subscriber cannot
  // change plan here at all — not via change-plan, and not by falling through to a fresh
  // checkout (that would silently reinterpret "switch my plan" as "buy a second plan"). Show a
  // read-only explanation and do nothing else. This state should not normally be reachable (the
  // billing page disables the CTAs that open this modal), but a deep link or the global paywall
  // host could still land here.
  const planChangeBlocked =
    onLivePaidPlan
    && !isSameTierAddOn
    && !(overviewQuery.data?.planChangeEnabled ?? false);
  const changingPlan =
    onLivePaidPlan &&
    !planChangeBlocked &&
    !isSameTierAddOn &&
    Boolean(targetPlanId) &&
    Boolean(selectedPlan) &&
    selectedPlan?.code !== currentPlanCode;

  // What the change costs today. Fetched only in change mode; `amountMinor` null = no quote.
  const previewQuery = useQuery({
    queryKey: ["plan-change-preview", targetPlanId],
    queryFn: () => api.previewPlanChange(targetPlanId as string),
    enabled: open && changingPlan,
    staleTime: 30_000,
    retry: false,
  });

  // Plan-change outcome, shown inline in the dialog (not a toast — a failed payment is too
  // important to risk missing). null while idle; the dialog stays open on "failed".
  const [confirmingChange, setConfirmingChange] = useState(false);
  const [changeOutcome, setChangeOutcome] = useState<
    | null
    | { kind: "failed"; message: string }
    | { kind: "processing"; message: string }
  >(null);

  const changePlan = useMutation({
    mutationFn: () => api.changePlan(targetPlanId as string),
    onSuccess: async () => {
      // The provider accepted the request, but an immediate upgrade's prorated charge settles
      // async — a decline comes back only via webhook, after this call already returned. Poll the
      // overview until the pending marker clears (confirmed) or flips to FAILED, so we never show
      // a stale "plan updated".
      // From here the button carries the feedback ("Confirming…"), so drop the full-screen loader
      // rather than holding it over a poll that can run 20s.
      endCheckoutActivity();
      setConfirmingChange(true);
      setChangeOutcome(null);
      const deadline = Date.now() + 20_000;
      const targetCode = selectedPlan?.code;
      try {
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 1500));
          const overview = await queryClient.fetchQuery({
            queryKey: ["billing-overview"],
            queryFn: () => api.getBillingOverview(),
          });
          const pending = overview.pendingPlanChange;
          if (pending?.status === "FAILED") {
            // Keep the dialog open with a prominent inline error — do not toast-and-close.
            // isSameTierAddOn never reaches this mutation (see the button that calls it below —
            // it only renders in the `changingPlan` branch, which excludes the add-on entirely),
            // so this is always a genuine plan switch.
            setChangeOutcome({
              kind: "failed",
              message: `The payment didn’t go through, so you’re still on ${overview.plan.name}. Update your card on file, then try switching again.`,
            });
            setConfirmingChange(false);
            return;
          }
          if (!pending || overview.plan.code === targetCode) {
            toasts.success("Plan updated", `You’re now on ${overview.plan.name}.`);
            setConfirmingChange(false);
            onClose();
            return;
          }
        }
      } catch {
        // fall through to the timeout branch
      }
      // Still pending after the window — likely fine, just slow.
      setChangeOutcome({
        kind: "processing",
        message:
          "Your plan will switch as soon as the payment clears — usually a moment. We’ll email you if anything goes wrong.",
      });
      setConfirmingChange(false);
    },
    onError: (e) => {
      setConfirmingChange(false);
      // The subscription has no Dodo subscription to modify in place — the customer has to pay
      // for the target plan through a fresh checkout. Hand off to that flow (it redirects to the
      // provider's hosted page); nothing is granted until that payment clears.
      // Keep the loader running through the hand-off: changePlan settling would otherwise clear
      // the button spinner and leave the modal sitting still for the frames before checkout
      // resolves and the redirect fires.
      if (e instanceof ApiError && e.code === "PLAN_CHANGE_REQUIRED") {
        beginCheckoutActivity();
        checkout.mutate();
        return;
      }
      endCheckoutActivity();
      setChangeOutcome({
        kind: "failed",
        message:
          e instanceof Error && e.message
            ? e.message
            : "Something went wrong and your plan is unchanged. Please try again.",
      });
    },
  });

  const validatePromo = useMutation({
    mutationFn: (code: string) => api.validatePromoCode(code, checkoutPlanId),
    onSuccess: (breakdown) => {
      setAppliedCode(breakdown.appliedPromoCode);
      if (breakdown.appliedPromoCode) {
        toasts.success("Promo applied", `Code ${breakdown.appliedPromoCode} will be used at checkout.`);
      }
    },
    onError: (e) =>
      toasts.error("Couldn’t apply promo", e instanceof Error ? e.message : "Please check the code and try again."),
  });

  const checkout = useMutation({
    mutationFn: () => api.startBillingCheckout(appliedCode ?? undefined, checkoutPlanId),
    onSuccess: async (res) => {
      // Persist the durable attempt id so that on return from the provider we can verify the
      // payment against the backend (confirm-then-poll) regardless of whether a webhook arrives,
      // plus where we were, so the confirmation can hand the user back to the screen they were
      // trying to unlock rather than stranding them on the billing page.
      rememberCheckout(res.checkoutAttemptId, window.location.pathname + window.location.search, selectedPlan?.tier);
      // Keep the full-screen loader mounted until the external document takes over.
      // Releasing it when the API request resolves exposes the upgrade dialog for one frame.
      await waitForNextPaint();
      try {
        window.location.assign(res.redirectUrl);
      } catch {
        endCheckoutActivity();
        toasts.error("Couldn’t open checkout", "Please try again.");
      }
    },
    onError: (e) => {
      endCheckoutActivity();
      // Surface the backend's reason ("You already have an active subscription", an invalid promo,
      // billing disabled…) rather than a blanket "try again". The dialog stays open so the user
      // can read it and decide what to do.
      const message =
        e instanceof Error && e.message ? e.message : "Something went wrong. Please try again.";
      toasts.error("Couldn’t start checkout", message);
    },
  });

  const startCheckout = () => {
    // A "switch my plan" click must never silently become a fresh checkout while in-place plan
    // change is gated off. The button that calls this is not rendered in that state, but guard
    // the mutation itself so nothing can.
    if (planChangeBlocked) return;
    if (checkoutActivityEndRef.current || checkout.isPending) return;
    // Begin in the click handler so visibility is not delayed by request scheduling
    // or the standard short-request anti-flicker timer.
    beginCheckoutActivity();
    checkout.mutate();
  };

  // Safety net: if the dialog closes with the loader still held but nothing in flight to redirect
  // (the caller closed it out of band), release it so the app is not left behind a permanent
  // full-screen loader. The checkout redirect path never flips `open` false — it navigates away
  // with the loader deliberately still up — so that case is untouched.
  useEffect(() => {
    if (!open && !checkout.isPending && !changePlan.isPending) endCheckoutActivity();
  }, [open, checkout.isPending, changePlan.isPending]);

  // The change-mode CTA. It carries the pre-tax amount from the preview so the number the user
  // commits to is on the button itself: "$10.00 + tax" for a prorated upgrade, "Switch at
  // renewal" for a scheduled downgrade (nothing charged now), plain "Switch plan" if no quote
  // came back. Tax is added on the provider's side, same as new checkout.
  //
  // Plan-switch only: isSameTierAddOn never reaches this CTA (or previewQuery — it's `enabled:
  // changingPlan`, which excludes the add-on entirely). The add-on's own price and verb are on
  // the plain-checkout CTA below instead ("Add - $30/yr", straight from priceOverrideMinor,
  // known from the first render — never a fetched preview).
  const previewMinor = previewQuery.data?.amountMinor ?? null;
  const previewCurrency = previewQuery.data?.currency ?? plan?.currency ?? "USD";
  // The proration, shown in the user's favour: new plan for the rest of the period, minus the
  // credit for time already paid on the current plan.
  const previewCredit = previewQuery.data?.creditMinor ?? 0;
  const previewNewPlan =
    previewQuery.data?.newPlanMinor ?? (previewMinor !== null ? previewMinor + previewCredit : 0);
  const isScheduled = Boolean(
    previewQuery.data?.effectiveAt &&
      new Date(previewQuery.data.effectiveAt).getTime() > Date.now() + 60_000,
  );
  const changePlanCtaLabel = previewQuery.isError
    ? "Switch plan"
    : isScheduled
      ? "Switch at renewal"
      : previewMinor && previewMinor > 0
        ? `Switch plan - ${formatPrice(previewMinor, previewCurrency)} + tax`
        : previewMinor === 0
          ? "Switch plan - nothing due today"
          : "Switch plan";

  return (
    <>
    {/* This dialog is mounted from several places (billing page, global paywall host); each has
        its own toast queue, and this component's `toasts` instance is not the same one those
        render. Render our own viewport here so checkout/promo errors actually appear — it
        portals to <body>, so it sits above the dialog. */}
    {toasts.viewport}
    <Dialog
      open={open}
      onClose={onClose}
      width="md"
      title={
        planChangeBlocked
          ? "Changing your plan"
          : isSameTierAddOn
            ? "Remove BunnyCal branding"
            : changingPlan
              ? `Switch to ${selectedPlan?.name ?? "a new plan"}`
              : `Upgrade to ${plan?.name ?? "Professional"}`
      }
      description={
        planChangeBlocked
          ? "Here's how to move to a different plan right now."
          : isSameTierAddOn
            ? "This adds to the plan you already have — you keep everything, plus the BunnyCal mark comes off your booking pages."
            : changingPlan
              ? "Change your plan without starting over."
              : "Everything you need to schedule without limits."
      }
      footer={
        planChangeBlocked ? (
          <div className="bil-dialog-actions">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : (
        <div className="bil-dialog-actions">
          <Button
            variant="ghost"
            disabled={checkout.isPending || changePlan.isPending || confirmingChange}
            onClick={onClose}
          >
            {changeOutcome ? "Close" : "Not now"}
          </Button>
          {changingPlan ? (
            changeOutcome?.kind === "processing" ? null : (
              <Button
                variant="primary"
                loading={changePlan.isPending || confirmingChange || previewQuery.isFetching}
                onClick={() => {
                  setChangeOutcome(null);
                  // Open the full-screen loader on the click itself. The add-on with no in-place
                  // path resolves to a provider redirect, and without this the modal sits still
                  // for the frames between changePlan erroring and checkout taking over.
                  beginCheckoutActivity();
                  changePlan.mutate();
                }}
              >
                {confirmingChange
                  ? "Confirming…"
                  : changeOutcome?.kind === "failed"
                    ? "Try again"
                    : changePlanCtaLabel}
              </Button>
            )
          ) : (
            <Button variant="primary" loading={checkout.isPending} onClick={startCheckout}>
              {plan
                ? `${isSameTierAddOn ? "Add" : "Upgrade"} - ${formatPrice(priceOverrideMinor ?? plan.amountMinor, plan.currency)}/${plan.billingInterval.toLowerCase()}`
                : isSameTierAddOn ? "Add" : "Upgrade"}
            </Button>
          )}
        </div>
        )
      }
    >
      {headline ? <p className="bil-upgrade-headline">{headline}</p> : null}

      {planChangeBlocked && (
        <div className="bil-change-banner" role="status">
          <span className="bil-change-banner-icon" aria-hidden="true">↔</span>
          <div>
            <strong>Changing your plan in place isn’t available right now</strong>
            <p>
              From the billing page, schedule your current subscription to end — you keep{" "}
              {overviewQuery.data?.plan?.name ?? "your plan"} until the end of your billing
              period — then choose a new plan. Need to switch sooner?{" "}
              <a href="mailto:support@bunnycal.io">Contact support</a>.
            </p>
          </div>
        </div>
      )}

      {!planChangeBlocked && changeOutcome && (
        <div
          className={
            changeOutcome.kind === "failed" ? "bil-change-banner is-error" : "bil-change-banner"
          }
          role="alert"
        >
          <span className="bil-change-banner-icon" aria-hidden="true">
            {changeOutcome.kind === "failed" ? "✕" : "⏳"}
          </span>
          <div>
            <strong>
              {changeOutcome.kind === "failed"
                ? "Your payment didn’t go through"
                : "Your change is processing"}
            </strong>
            <p>{changeOutcome.message}</p>
          </div>
        </div>
      )}

      {confirmingChange && !changeOutcome && (
        <div className="bil-change-banner" role="status">
          <span className="bil-change-banner-spinner" aria-hidden="true" />
          <div>
            <strong>Confirming your change…</strong>
            <p>We’re checking the payment with our provider. This usually takes a few seconds.</p>
          </div>
        </div>
      )}

      {changingPlan && !upgradingFromTrial && !changeOutcome && !confirmingChange ? (
        isScheduled ? (
          <p className="bil-muted">
            Your current plan stays active until it renews, then switches to{" "}
            {selectedPlan?.name} automatically. Nothing is charged today.
          </p>
        ) : previewQuery.isFetching && previewMinor === null ? (
          <p className="bil-muted">Working out what you’ll pay…</p>
        ) : previewMinor !== null ? (
          <>
            <p className="bil-muted">
              This change takes effect right away. You only pay the difference for the rest of this
              billing period — we credit the unused time on your current plan.
            </p>
            <dl className="bil-cost-lines">
              {previewCredit > 0 ? (
                <>
                  <div>
                    <dt>{selectedPlan?.name} for the rest of this period</dt>
                    <dd>{formatPrice(previewNewPlan, previewCurrency)}</dd>
                  </div>
                  <div className="bil-cost-discount">
                    <dt>Credit for your unused time</dt>
                    <dd>−{formatPrice(previewCredit, previewCurrency)}</dd>
                  </div>
                </>
              ) : (
                <div>
                  <dt>{selectedPlan?.name} for the rest of this period</dt>
                  <dd>{formatPrice(previewMinor, previewCurrency)}</dd>
                </div>
              )}
              <div className="bil-cost-muted">
                <dt>Tax</dt>
                <dd>calculated at checkout</dd>
              </div>
              <div className="bil-cost-total">
                <dt>Due today</dt>
                <dd>
                  {previewMinor > 0
                    ? `from ${formatPrice(previewMinor, previewCurrency)}`
                    : formatPrice(0, previewCurrency)}
                </dd>
              </div>
            </dl>
            {previewCredit > 0 && (
              <p className="bil-cost-note">
                {previewMinor > 0 ? (
                  <>
                    You save {formatPrice(previewCredit, previewCurrency)} today — the time you’ve
                    already paid for on {overviewQuery.data?.plan.name} carries over.
                  </>
                ) : (
                  <>
                    Nothing to pay today — your {overviewQuery.data?.plan.name} credit covers the
                    switch, and anything left over applies to your next bill.
                  </>
                )}
              </p>
            )}
          </>
        ) : (
          // isSameTierAddOn is never true here: it is excluded from `changingPlan` entirely (see
          // that definition above) — it renders through the plain-checkout branch below instead,
          // same as this modal's ordinary "Upgrade to X" flow, just with the add-on's own copy.
          <p className="bil-muted">
            This change takes effect right away. Your card on file is charged the prorated
            difference; the exact amount is shown at checkout.
          </p>
        )
      ) : upgradingFromTrial ? (
        <p className="bil-muted">
          Upgrading starts your paid subscription immediately and ends your free trial.
        </p>
      ) : isSameTierAddOn ? (
        <p className="bil-muted">
          This turns the option on right away. Your card on file is charged in full — no proration
          against your current plan.
        </p>
      ) : null}
      {!planChangeBlocked && !confirmingChange && !changeOutcome && (
        <ul className="bil-feature-list">
          {(isSameTierAddOn
            ? ["Your BunnyCal mark comes off every booking page and embed", "Everything else on your plan stays exactly the same"]
            : PRO_FEATURES
          ).map((f) => (
            <li key={f}>
              <span className="bil-check" aria-hidden="true">✓</span>
              {f}
            </li>
          ))}
        </ul>
      )}

      {!planChangeBlocked && !changingPlan && plan && (
        <>
          {(() => {
            // Line-item breakdown shown before the redirect. Tax is finalised on the provider's
            // page (it depends on the customer's location and our backend carries it as 0), so
            // this shows the subtotal and flags tax as pending rather than inventing a total.
            const promo =
              validatePromo.isSuccess && validatePromo.data.appliedPromoCode
                ? validatePromo.data
                : null;
            const priceMinor = priceOverrideMinor ?? plan.amountMinor;
            const discountMinor = promo ? promo.couponMinor + promo.manualMinor : 0;
            const subtotalMinor = promo ? promo.totalMinor : priceMinor;
            const per = plan.billingInterval.toLowerCase();
            return (
              <dl className="bil-cost-lines">
                <div>
                  {/* The plan's own catalog name (e.g. "Professional + branding removed") reads
                      as a plan-shaped line for the add-on it isn't — it's an addition, not a
                      plan of its own. */}
                  <dt>{isSameTierAddOn ? "Remove BunnyCal branding" : `${plan.name} plan`}</dt>
                  <dd>
                    {formatPrice(priceMinor, plan.currency)} / {per}
                  </dd>
                </div>
                {discountMinor > 0 && (
                  <div className="bil-cost-discount">
                    <dt>Promo {promo?.appliedPromoCode}</dt>
                    <dd>−{formatPrice(discountMinor, plan.currency)}</dd>
                  </div>
                )}
                {discountMinor > 0 && (
                  <div>
                    <dt>Subtotal</dt>
                    <dd>{formatPrice(subtotalMinor, plan.currency)}</dd>
                  </div>
                )}
                <div className="bil-cost-muted">
                  <dt>Tax</dt>
                  <dd>calculated at checkout</dd>
                </div>
                <div className="bil-cost-total">
                  <dt>Total today</dt>
                  <dd>from {formatPrice(subtotalMinor, plan.currency)}</dd>
                </div>
              </dl>
            );
          })()}

          <div className="bil-promo">
            <Input
              placeholder="Promo code"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value.toUpperCase());
                if (appliedCode) setAppliedCode(null);
                validatePromo.reset();
              }}
            />
            <Button
              variant="secondary"
              loading={validatePromo.isPending}
              disabled={!promoCode.trim()}
              onClick={() => validatePromo.mutate(promoCode.trim())}
            >
              Apply
            </Button>
          </div>
        </>
      )}
    </Dialog>
    </>
  );
}
